import axios from 'axios'
import { isEmpty, isMatch, isNil, isString, omit, pickBy } from 'lodash'

import { Company } from '../models/companyModel'
import { InvoiceGateway, UpdateAttachments } from '../models/invoiceModels'
import { LoanGateway } from '../models/loanModels'
import { ReceivableGateway } from '../models/receivableModels'
import { Attachment, AttachmentGateway, AttachmentWithIdsGateway, UploadFileGateway } from '../models/uploadModels'
import { makaluApi } from '../utils/makaluApiClient'

export const uploadFiles = async (
    files: (File | Blob)[] | undefined,
    companyId: Company['id']
): Promise<UploadFileGateway[] | undefined> => {
    const filesResponse: (UploadFileGateway | undefined)[] = []
    try {
        if (isNil(files) || isEmpty(files)) return undefined
        for (const file of files) {
            const formattedFile = new FormData()
            formattedFile.append('file', file)
            const uploads: UploadFileGateway[] = await makaluApi.POST('/companies/{companyId}/upload', {
                params: { path: { companyId } },
                body: formattedFile
            })
            filesResponse.push(uploads[0])
        }
        return filesResponse.filter((file): file is UploadFileGateway => !isNil(file))
    } catch (error) {
        console.error('uploadFiles' + error)
        return filesResponse.filter((file): file is UploadFileGateway => !isNil(file))
    }
}

export const downloadAttachments = async (
    attachments: string[] | AttachmentGateway[] | AttachmentWithIdsGateway[] | null | undefined
): Promise<Attachment[] | undefined> => {
    try {
        if (isEmpty(attachments) || !attachments) {
            return []
        }

        return await Promise.all(
            attachments.map(async (attachment) => {
                const url = isString(attachment) ? attachment : (attachment.url ?? '')

                const response = await axios(url, { responseType: 'blob' })

                const contentDisposition = response.headers['content-disposition']
                let filename = ''
                if (contentDisposition) {
                    const matches = contentDisposition.match(/filename="([^"]+)"/)
                    filename = matches ? matches[1] : ''
                }

                filename = decodeURIComponent(filename).replace(/%22/g, '')

                const contentType = response.headers['content-type']
                const file = new File([response.data], filename, {
                    type: typeof contentType === 'string' ? contentType : undefined
                })

                if (isString(attachment)) {
                    return { url: attachment, file }
                }

                if ('id' in attachment) {
                    return {
                        ...attachment,
                        file,
                        url: attachment.url ?? ''
                    }
                }

                return { ...attachment, file }
            })
        )
    } catch (error) {
        console.error('downloadAttachments: ' + error)
        throw error
    }
}

export type State = 'update' | 'delete'

function removeAttachmentFromList(attachments: Attachment[], attachment: Attachment): Attachment[] {
    const toRemoveCriteria = pickBy(
        {
            file: attachment.file,
            url: attachment.url,
            type: attachment.type
        },
        (value) => !isNil(value)
    )
    return attachments.filter((attachment) => !isMatch(attachment, toRemoveCriteria))
}

const toUpdatedAttachments = async (
    companyId: string,
    { attachments, attachment }: Pick<AttachmentsUpdate, 'attachments' | 'attachment'>,
    state: State
): Promise<AttachmentGateway[]> => {
    switch (state) {
        case 'delete':
            return removeAttachmentFromList(attachments ?? [], attachment)

        case 'update': {
            const existingAttachments = attachments?.map((attachment) => omit(attachment, ['file'])) ?? []

            let newAttachments: AttachmentGateway[] = []
            if (attachment?.file) {
                const uploadResult = await uploadFiles([attachment.file], companyId)
                if (isEmpty(uploadResult)) {
                    throw new Error('Failed to upload new attachments')
                }
                newAttachments =
                    uploadResult?.map((upload) => ({
                        url: upload.url,
                        id: upload.fileId
                    })) || []
            }

            return [...existingAttachments, ...newAttachments]
        }

        default: {
            const unknownState: never = state
            throw new Error(`Invalid state: ${String(unknownState)}`)
        }
    }
}

type AttachmentsUpdate = UpdateAttachments<{ id: string; attachments?: Attachment[] }>

function requireUpdated<Resource>(resource: Resource | undefined): Resource {
    if (isNil(resource)) throw new Error('Invalid response from server')
    return resource
}

export const updateLoanAttachments = async (
    companyId: string,
    payload: AttachmentsUpdate,
    state: State
): Promise<LoanGateway> => {
    const attachments = await toUpdatedAttachments(companyId, payload, state)
    const updated = await makaluApi.PATCH('/companies/{companyId}/loans/{loanId}', {
        params: { path: { companyId, loanId: payload.id } },
        body: { attachments }
    })
    return requireUpdated(updated)
}

export const updateInvoiceAttachments = async (
    companyId: string,
    payload: AttachmentsUpdate,
    state: State
): Promise<InvoiceGateway> => {
    const attachments = await toUpdatedAttachments(companyId, payload, state)
    const updated = await makaluApi.PATCH('/companies/{companyId}/invoices/{invoiceId}', {
        params: { path: { companyId, invoiceId: payload.id } },
        body: { attachments }
    })
    return requireUpdated(updated)
}

// A receivable references its attachments by file id only (`AttachmentWithIdSchema`).
const requireAttachmentId = ({ id }: AttachmentGateway): { id: string } => {
    if (isNil(id)) throw new Error('A receivable attachment needs its file id')
    return { id }
}

export const updateReceivableAttachments = async (
    companyId: string,
    payload: AttachmentsUpdate,
    state: State
): Promise<ReceivableGateway> => {
    const attachments = await toUpdatedAttachments(companyId, payload, state)
    const updated = await makaluApi.PATCH('/companies/{companyId}/receivables/{receivableId}', {
        params: { path: { companyId, receivableId: payload.id } },
        body: { attachments: attachments.map(requireAttachmentId) }
    })
    return requireUpdated(updated)
}
